import { Box } from '@mui/material';
import React from 'react';
import { protoIcon } from './ProtoIcon';

/**
 * Peças extras do kit mobile (`prototipos-mobile/assets/mobile.css`) usadas pelas
 * telas de Vendas / PDV: `.chips/.chip`, `.search`, `.field/.input/.select/.textarea`,
 * `.stepper`, `.kv`, `.timeline`, `.ecard`, `.banner`. Valores copiados 1:1 do CSS
 * do protótipo (claro e escuro via theme.palette). Só devem ser usadas no shell mobile.
 */

const SearchIcon = protoIcon('search');
const Plus = protoIcon('plus');
const Minus = protoIcon('minus');

const SHADOW_SM = '0 1px 2px rgba(15,23,42,.06)';
const WARN_LIGHT = '#b45309';

// `.chips` — linha rolável de chips (padding/margin negativa casam com o padding de 16px da tela).
export function KitChips({ children, sx }) {
    return (
        <Box sx={{ display: 'flex', gap: '8px', overflowX: 'auto', p: '2px 16px 4px', m: '0 -16px', ...sx }}>
            {children}
        </Box>
    );
}

// `.chip` (+ `.active`, `.n`)
export function KitChip({ active, count, children, onClick }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={Boolean(active)}
            sx={(theme) => {
                const p = theme.palette;
                return {
                    flexShrink: 0,
                    height: 36,
                    p: '0 14px',
                    borderRadius: '18px',
                    backgroundColor: active ? p.primary.main : p.background.paper,
                    border: '1px solid',
                    borderColor: active ? p.primary.main : p.divider,
                    color: active ? '#fff' : p.text.secondary,
                    fontWeight: 600,
                    fontSize: 13,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                    boxSizing: 'border-box',
                    whiteSpace: 'nowrap',
                };
            }}
        >
            {children}
            {count !== undefined && count !== null && (
                <Box
                    component="span"
                    sx={(theme) => ({
                        backgroundColor: active ? 'rgba(255,255,255,.25)' : theme.palette.background.subtle,
                        color: active ? '#fff' : theme.palette.text.secondary,
                        borderRadius: '9px',
                        p: '0 6px',
                        fontSize: 11,
                        height: 18,
                        display: 'inline-flex',
                        alignItems: 'center',
                    })}
                >
                    {count}
                </Box>
            )}
        </Box>
    );
}

// `.search` — campo de busca da barra de ferramentas (46px, raio 14)
export function KitSearch({ value, onChange, placeholder, onKeyDown, inputProps, sx }) {
    return (
        <Box
            component="label"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                height: 46,
                borderRadius: '14px',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                p: '0 14px',
                color: 'text.disabled',
                boxSizing: 'border-box',
                flex: 1,
                minWidth: 0,
                ...sx,
            }}
        >
            <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}><SearchIcon size={20} /></Box>
            <Box
                component="input"
                value={value}
                onChange={onChange}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                {...inputProps}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    border: 0,
                    outline: 0,
                    backgroundColor: 'transparent',
                    color: 'text.primary',
                    fontFamily: 'inherit',
                    // 16px (e não os 14px do protótipo): abaixo disso o iOS dá zoom ao focar.
                    fontSize: 16,
                    p: 0,
                    // Placeholder nos 14px do protótipo (o texto digitado segue em 16px).
                    '&::placeholder': { color: 'text.disabled', opacity: 1, fontSize: 14 },
                }}
            />
        </Box>
    );
}

// `.toolbar .icon-btn` — botão quadrado ao lado da busca (46x46, raio 14, borda)
export function KitToolbarButton({ children, sx, ...rest }) {
    return (
        <Box
            component="button"
            type="button"
            {...rest}
            sx={{
                width: 46,
                height: 46,
                borderRadius: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                boxSizing: 'border-box',
                flexShrink: 0,
                p: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
                '&:active': { backgroundColor: 'background.subtle' },
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

// `.sticky-top` + `.toolbar`: fica colado abaixo da barra superior do shell (`top`).
export function KitStickyToolbar({ top, children }) {
    return (
        <Box
            sx={{
                position: 'sticky',
                top,
                zIndex: 3,
                backgroundColor: 'background.default',
                m: '-16px -16px 12px',
                p: '12px 16px 8px',
            }}
        >
            <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center' }}>{children}</Box>
        </Box>
    );
}

// `.field` + `label` (11px/800 uppercase) + `.help`
export function KitField({ label, required, help, error, children, sx }) {
    return (
        <Box sx={{ display: 'block', mb: '14px', ...sx }}>
            {label && (
                <Box
                    component="label"
                    sx={{ display: 'block', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.05em', color: 'text.disabled', m: '0 2px 6px', lineHeight: 1.4 }}
                >
                    {label}
                    {required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
                </Box>
            )}
            {children}
            {help && (
                <Box sx={{ fontSize: 12, color: error ? 'error.main' : 'text.disabled', m: '5px 2px 0', lineHeight: 1.4 }}>{help}</Box>
            )}
        </Box>
    );
}

const inputBaseSx = {
    width: '100%',
    height: 50,
    borderRadius: '14px',
    border: '1.5px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    p: '0 14px',
    outline: 0,
    // 16px (protótipo: 15px) para o iOS não dar zoom ao focar.
    fontSize: 16,
    color: 'text.primary',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    appearance: 'none',
    WebkitAppearance: 'none',
    // Ícone nativo do calendário/hora legível no tema escuro.
    colorScheme: (theme) => theme.palette.mode,
    '&:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 4px rgba(37,99,235,.12)' },
    '&::placeholder': { color: 'text.disabled', opacity: 1 },
    '&:disabled': { opacity: 0.6 },
};

// `.input`
export const KitInput = React.forwardRef(function KitInput({ error, sx, ...rest }, ref) {
    return <Box component="input" ref={ref} {...rest} sx={{ ...inputBaseSx, ...(error ? { borderColor: 'error.main' } : {}), ...sx }} />;
});

const CHEVRON_DOWN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")";

// `.select`
export function KitSelect({ children, sx, ...rest }) {
    return (
        <Box
            component="select"
            {...rest}
            sx={{
                ...inputBaseSx,
                backgroundImage: CHEVRON_DOWN,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 14px center',
                pr: '40px',
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

// `.textarea`
export function KitTextarea({ sx, ...rest }) {
    return (
        <Box
            component="textarea"
            {...rest}
            sx={{ ...inputBaseSx, height: 'auto', minHeight: 92, p: '12px 14px', resize: 'vertical', display: 'block', ...sx }}
        />
    );
}

// `.stepper` (botões 32x32; `small` usa ícones de 14px como o carrinho do protótipo)
export function KitStepper({ value, onMinus, onPlus, minusDisabled, plusDisabled, small, minusLabel = 'Diminuir', plusLabel = 'Aumentar' }) {
    const btnSx = {
        width: 32,
        height: 32,
        borderRadius: '9px',
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: SHADOW_SM,
        color: 'primary.main',
        border: 0,
        p: 0,
        cursor: 'pointer',
        fontFamily: 'inherit',
        '&:disabled': { opacity: 0.45, cursor: 'default' },
    };
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: 'background.subtle',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                p: '3px',
                flexShrink: 0,
            }}
        >
            <Box component="button" type="button" aria-label={minusLabel} onClick={onMinus} disabled={minusDisabled} sx={btnSx}>
                <Minus size={small ? 14 : 16} />
            </Box>
            <Box component="b" sx={{ minWidth: 28, textAlign: 'center', fontWeight: 800, fontSize: 14, lineHeight: 1.4 }}>{value}</Box>
            <Box component="button" type="button" aria-label={plusLabel} onClick={onPlus} disabled={plusDisabled} sx={btnSx}>
                <Plus size={small ? 14 : 16} />
            </Box>
        </Box>
    );
}

// `.kv` (+ `.totals .kv`, `.kv.big`)
export function KitKV({ label, value, totals, big, warn, sx }) {
    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '12px',
                fontSize: big ? 17 : 13.5,
                fontWeight: big ? 800 : undefined,
                lineHeight: 1.4,
                ...(totals
                    ? { p: big ? '12px 0 6px' : '6px 0', border: 0, ...(big ? { borderTop: '1px dashed', borderColor: 'divider', mt: '6px' } : {}) }
                    : { p: '9px 0', borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }),
                ...sx,
            }}
        >
            <Box component="span" sx={{ color: big ? 'text.primary' : 'text.disabled' }}>{label}</Box>
            <Box component="span" sx={{ fontWeight: 600, textAlign: 'right', ...(warn ? { color: WARN_LIGHT } : {}) }}>{value}</Box>
        </Box>
    );
}

// `.timeline` / `.tl` — `state`: 'done' | 'off' | undefined (marcador azul)
export function KitTimeline({ items }) {
    return (
        <Box sx={{ position: 'relative', pl: '26px', '&::before': { content: '""', position: 'absolute', left: '9px', top: '6px', bottom: '6px', width: '2px', backgroundColor: 'divider' } }}>
            {items.map((item, index) => (
                <Box
                    key={`${item.title}-${index}`}
                    sx={(theme) => {
                        const p = theme.palette;
                        const color = item.state === 'done' ? p.success.main : item.state === 'off' ? p.divider : p.primary.main;
                        return {
                            position: 'relative',
                            pb: index === items.length - 1 ? 0 : '16px',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: '-23px',
                                top: '3px',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                boxSizing: 'border-box',
                                backgroundColor: item.state === 'done' ? p.success.main : p.background.paper,
                                border: '3px solid',
                                borderColor: color,
                            },
                        };
                    }}
                >
                    <Box component="b" sx={{ display: 'block', fontSize: 13.5, lineHeight: 1.4 }}>{item.title}</Box>
                    {item.sub && <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>{item.sub}</Box>}
                </Box>
            ))}
        </Box>
    );
}

// `.banner` (+ `.danger`)
export function KitBanner({ tone, title, icon: Icon, iconSize = 20, children }) {
    return (
        <Box
            sx={(theme) => {
                const p = theme.palette;
                const dark = p.mode === 'dark';
                let bg = p.warning.bg;
                let fg = dark ? p.warning.main : '#92400e';
                if (tone === 'danger') { bg = p.error.bg; fg = p.error.main; }
                else if (tone === 'info') { bg = p.info.bg; fg = p.primary.main; }
                else if (tone === 'success') { bg = p.success.bg; fg = p.success.main; }
                return { display: 'flex', gap: '12px', alignItems: 'flex-start', p: '12px 14px', borderRadius: '14px', backgroundColor: bg, color: fg, fontSize: 12.5, lineHeight: 1.4 };
            }}
        >
            {Icon && <Icon size={iconSize} />}
            <Box>
                {title && <Box component="b" sx={{ display: 'block', fontSize: 13 }}>{title}</Box>}
                {children}
            </Box>
        </Box>
    );
}

// `.ecard` (+ `.stripe`) — cartão de entidade (venda, OS, orçamento)
export function KitEntityCard({ stripeColor, onClick, children, sx }) {
    return (
        <Box
            component="a"
            role="link"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' && onClick) onClick(e); }}
            sx={{
                display: 'block',
                position: 'relative',
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '14px',
                boxShadow: SHADOW_SM,
                boxSizing: 'border-box',
                color: 'text.primary',
                cursor: 'pointer',
                textDecoration: 'none',
                fontSize: 14,
                lineHeight: 1.4,
                ...(stripeColor
                    ? { '&::before': { content: '""', position: 'absolute', left: 0, top: '14px', bottom: '14px', width: 4, borderRadius: '0 4px 4px 0', backgroundColor: stripeColor } }
                    : {}),
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

// `.fab` — botão flutuante em pílula (gradiente da marca). Fixo no canto inferior direito.
export function KitFab({ label, icon: Icon, onClick, aboveTabbar }) {
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            aria-label={label}
            sx={(theme) => ({
                position: 'fixed',
                right: 'calc(16px + env(safe-area-inset-right, 0px))',
                // `.screen.has-tabbar .fab`: acima da barra inferior (64px) + 18px.
                bottom: aboveTabbar ? 'calc(82px + env(safe-area-inset-bottom, 0px))' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
                height: 52,
                p: '0 20px 0 16px',
                borderRadius: '26px',
                backgroundImage: theme.palette.gradient.brand,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: 'inherit',
                border: 0,
                boxShadow: '0 10px 24px rgba(37,99,235,.4)',
                zIndex: theme.zIndex.appBar - 1,
                cursor: 'pointer',
                boxSizing: 'border-box',
            })}
        >
            {Icon && <Icon size={22} />}
            {label}
        </Box>
    );
}

// `.formbar` — barra de ações fixa no rodapé (largura total, botões de 48px lado a lado).
// Fixa na viewport; o espaçador evita que cubra o fim do conteúdo.
export function KitFormBar({ children }) {
    return (
        <>
            <Box aria-hidden sx={{ height: 'calc(58px + env(safe-area-inset-bottom, 0px))', flexShrink: 0 }} />
            <Box
                sx={(theme) => ({
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: theme.zIndex.appBar,
                    display: 'flex',
                    gap: '10px',
                    p: '10px 16px calc(12px + env(safe-area-inset-bottom, 0px))',
                    pl: 'calc(16px + env(safe-area-inset-left, 0px))',
                    pr: 'calc(16px + env(safe-area-inset-right, 0px))',
                    backgroundColor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 -8px 24px rgba(15,23,42,.05)',
                    boxSizing: 'border-box',
                    '& > *': { flex: 1 },
                })}
            >
                {children}
            </Box>
        </>
    );
}

// `.seg` — controle segmentado (altura 36, raio 11; ativo = superfície + marca)
export function KitSeg({ options, value, onChange, sx }) {
    return (
        <Box
            role="tablist"
            sx={{
                display: 'flex',
                backgroundColor: 'background.subtle',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '14px',
                p: '3px',
                gap: '2px',
                ...sx,
            }}
        >
            {options.map((option) => {
                const active = option.value === value;
                return (
                    <Box
                        key={option.value}
                        component="button"
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(option.value)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: 36,
                            borderRadius: '11px',
                            border: 0,
                            fontWeight: 700,
                            fontSize: 13,
                            fontFamily: 'inherit',
                            cursor: 'pointer',
                            backgroundColor: active ? 'background.paper' : 'transparent',
                            color: active ? 'primary.main' : 'text.secondary',
                            boxShadow: active ? SHADOW_SM : 'none',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.4,
                        }}
                    >
                        {option.label}
                    </Box>
                );
            })}
        </Box>
    );
}

// `.empty` — estado vazio centralizado (ícone 72px em círculo + título + texto)
export function KitEmpty({ icon: Icon, title, children }) {
    return (
        <Box sx={{ textAlign: 'center', p: '36px 20px', color: 'text.secondary' }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: 'info.bg', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', m: '0 auto 14px' }}>
                {Icon && <Icon size={32} />}
            </Box>
            <Box component="h3" sx={{ m: '0 0 4px', fontSize: 16, fontWeight: 800, color: 'text.primary', lineHeight: 1.4 }}>{title}</Box>
            {children && <Box component="p" sx={{ m: '0 0 16px', fontSize: 13, lineHeight: 1.4 }}>{children}</Box>}
        </Box>
    );
}

// `.progress` (+ `.amber`/`.green`/`.red`) — barra de 8px
export function KitProgress({ value, tone, sx }) {
    return (
        <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: 'background.subtle', overflow: 'hidden', ...sx }}>
            <Box
                sx={(theme) => ({
                    height: '100%',
                    borderRadius: '4px',
                    width: `${Math.max(0, Math.min(100, value))}%`,
                    ...(tone === 'amber' ? { backgroundColor: theme.palette.warning.main }
                        : tone === 'green' ? { backgroundColor: theme.palette.success.main }
                            : tone === 'red' ? { backgroundColor: theme.palette.error.main }
                                : { backgroundImage: theme.palette.gradient.brand }),
                })}
            />
        </Box>
    );
}

// `.plate` — placa em monoespaçada com borda de 2px
export function KitPlate({ children }) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontWeight: 800,
                letterSpacing: '.08em',
                border: '2px solid',
                borderColor: 'text.primary',
                borderRadius: '6px',
                p: '1px 8px',
                fontSize: 12,
                backgroundColor: 'background.paper',
                lineHeight: 1.4,
            }}
        >
            {children}
        </Box>
    );
}

// `.avatar.soft|purple|green|amber|red` — fundo/cor da variante; sem `tone` = gradiente da marca.
function avatarToneStyle(theme, tone) {
    const p = theme.palette;
    switch (tone) {
        case 'soft': return { backgroundColor: p.info.bg, color: p.primary.main };
        case 'purple': return { backgroundColor: p.secondary.bg, color: p.secondary.main };
        case 'green': return { backgroundColor: p.success.bg, color: p.success.main };
        case 'amber': return { backgroundColor: p.warning.bg, color: p.warning.main };
        case 'red': return { backgroundColor: p.error.bg, color: p.error.main };
        default: return { backgroundImage: p.gradient.brand, color: '#fff' };
    }
}

// `.avatar` — círculo com iniciais (gradiente da marca). `size` 56 = `.lg`.
export function KitAvatar({ text, size = 56, tone, sx }) {
    return (
        <Box
            component="span"
            sx={(theme) => ({
                width: size,
                height: size,
                borderRadius: '50%',
                ...avatarToneStyle(theme, tone),
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
                fontSize: size >= 76 ? 26 : size >= 56 ? 20 : 13,
                flexShrink: 0,
                ...sx,
            })}
        >
            {text}
        </Box>
    );
}

const ChevronDownIcon = protoIcon('down');

// `details.acc` — acordeão de formulário (ícone com gradiente, título/subtítulo, chevron que gira)
export function KitAccordion({ open, icon: Icon, title, subtitle, onToggle, children }) {
    return (
        <Box
            sx={{
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                mb: '10px',
                boxShadow: SHADOW_SM,
                overflow: 'hidden',
            }}
        >
            <Box
                component="button"
                type="button"
                aria-expanded={open}
                onClick={onToggle}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    p: '14px',
                    width: '100%',
                    boxSizing: 'border-box',
                    border: 0,
                    background: 'none',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    color: 'text.primary',
                    cursor: 'pointer',
                }}
            >
                <Box
                    component="span"
                    sx={(theme) => ({
                        width: 36,
                        height: 36,
                        borderRadius: '11px',
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    })}
                >
                    <Icon size={20} />
                </Box>
                <Box sx={{ minWidth: 0, fontSize: 14, lineHeight: 1.4 }}>
                    <Box component="b" sx={{ display: 'block', fontSize: 14, fontWeight: 700 }}>{title}</Box>
                    <Box component="small" sx={{ display: 'block', color: 'text.disabled', fontSize: 12, lineHeight: '19.6px' }}>{subtitle}</Box>
                </Box>
                <Box sx={{ ml: 'auto', display: 'inline-flex', color: 'text.disabled', transition: '.2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                    <ChevronDownIcon size={18} />
                </Box>
            </Box>
            {open && (
                <Box sx={{ p: '14px 14px 6px', borderTop: '1px solid', borderColor: 'divider' }}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

// `.grid2`
export function KitGrid2({ children }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', '& > *': { minWidth: 0 } }}>{children}</Box>
    );
}

// `.input-ico` — ícone decorativo à esquerda (`icon`) e/ou botões à direita (`.btn-in`, 38x38).
// `rightCount` reserva o espaço dos botões (`.input-ico.right .input{padding-right:48px}`).
export function KitInputIco({ icon: Icon, rightButtons, children }) {
    return (
        <Box sx={{ position: 'relative' }}>
            {Icon && (
                <Box sx={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'text.disabled', display: 'inline-flex', pointerEvents: 'none', zIndex: 1 }}>
                    <Icon size={20} />
                </Box>
            )}
            {children}
            {rightButtons && (
                <Box sx={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>
                    {rightButtons}
                </Box>
            )}
        </Box>
    );
}

// `.btn-in` — botão dentro do campo (38x38, raio 10, cor da marca)
export function KitInputButton({ children, sx, ...rest }) {
    return (
        <Box
            component="button"
            type="button"
            {...rest}
            sx={{
                width: 38,
                height: 38,
                borderRadius: '10px',
                border: 0,
                background: 'none',
                p: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'primary.main',
                cursor: 'pointer',
                '&:disabled': { opacity: 0.6, cursor: 'default' },
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

// `.switch` + `.tg` — linha com título/descrição e toggle 46x28
export function KitSwitch({ title, description, checked, onChange, disabled, ariaLabel }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '12px 0' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box component="b" sx={{ display: 'block', fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{title}</Box>
                <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.4 }}>{description}</Box>
            </Box>
            <Box component="label" sx={{ position: 'relative', width: 46, height: 28, flexShrink: 0, opacity: disabled ? 0.5 : 1 }}>
                <Box
                    component="input"
                    type="checkbox"
                    checked={Boolean(checked)}
                    onChange={onChange}
                    disabled={disabled}
                    aria-label={ariaLabel || title}
                    sx={{ position: 'absolute', opacity: 0, inset: 0, width: '100%', height: '100%', m: 0, zIndex: 2, cursor: disabled ? 'default' : 'pointer' }}
                />
                <Box
                    component="i"
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '14px',
                        backgroundColor: checked ? 'primary.main' : 'divider',
                        transition: '.2s',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '3px',
                            left: checked ? '21px' : '3px',
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                            transition: '.2s',
                        },
                    }}
                />
            </Box>
        </Box>
    );
}

// `.hero-photo` — área tracejada de upload/foto (altura 130 por padrão)
export function KitDropzone({ icon: Icon, iconSize = 28, height = 130, onClick, children, sx, ...rest }) {
    return (
        <Box
            component={onClick ? 'button' : 'div'}
            type={onClick ? 'button' : undefined}
            onClick={onClick}
            {...rest}
            sx={{
                height,
                width: '100%',
                borderRadius: '16px',
                backgroundColor: 'background.subtle',
                border: '2px dashed',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
                gap: '6px',
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.4,
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                p: 0,
                cursor: onClick ? 'pointer' : 'default',
                overflow: 'hidden',
                position: 'relative',
                '& b': { fontWeight: 700 },
                ...sx,
            }}
        >
            {Icon && <Icon size={iconSize} />}
            {children}
        </Box>
    );
}

// `.thumb-sq` — miniatura quadrada 44x44 (raio 12)
export function KitThumb({ icon: Icon, iconSize = 22, size = 44, children, sx }) {
    return (
        <Box
            component="span"
            sx={{
                width: size,
                height: size,
                borderRadius: '12px',
                backgroundColor: 'background.subtle',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.disabled',
                flexShrink: 0,
                overflow: 'hidden',
                ...sx,
            }}
        >
            {Icon && <Icon size={iconSize} />}
            {children}
        </Box>
    );
}

// `.wizard` + `.wiz-label` — barra de passos (segmentos de 4px) e legenda
export function KitWizard({ step, total = 3, label }) {
    return (
        <>
            <Box sx={{ display: 'flex', gap: '6px', mb: '6px' }}>
                {Array.from({ length: total }, (_, i) => (
                    <Box key={i} component="span" sx={{ flex: 1, height: 4, borderRadius: '2px', backgroundColor: i < step ? 'primary.main' : 'divider' }} />
                ))}
            </Box>
            <Box sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 600, mb: '14px', lineHeight: 1.4 }}>{label}</Box>
        </>
    );
}
